import {
  CommandExecuteEvent,
  ClassicEditor,
  InlineEditor,
  ViewDocumentArrowKeyEvent,
  ContextualBalloon,
  getLocalizedArrowKeyCodeDirection,
  ViewRootEditableElement,
} from "ckeditor5";

import { Editor } from "../types";

export const preventToolbarFlicker = (editor: Editor) => {
  if (!(editor instanceof InlineEditor)) return;

  //
  // CKE's InlineEditor implementation shows the toolbar panel before it is positioned
  // causing a brief flash of it in the corner
  // To work around, we hide it until the first position change
  // See EditorStyles.css for the directives
  //
  const toolbarPanel = editor.ui.view.panel;
  toolbarPanel.class = "rce-toolbar_positioning";
  toolbarPanel.once("change:position", () => {
    toolbarPanel.class = undefined;
  });
};

export const preventBalloonToolbarOverlap = (editor: Editor) => {
  if (!(editor instanceof InlineEditor) && !(editor instanceof ClassicEditor)) {
    return;
  }

  //
  // Set a custom balloon limiter to keep balloons from overlapping the toolbar
  //
  // Note: this implementation should support multi-root editors, although we
  // don't use that functionality right now
  //
  const view = editor.editing.view;
  const limiterElements = new Map<ViewRootEditableElement, HTMLElement>();

  for (const root of view.document.roots) {
    const domRoot = view.domConverter.mapViewToDom(root);
    if (domRoot?.parentElement == null) continue;

    //
    // Create an element that is the same size and position as the editor area,
    // except with the top and bottom adjusted enough that the balloon will clear
    // the toolbar and be allowed to extend past the bottom some
    //
    const limiterElement = document.createElement("div");
    Object.assign(limiterElement.style, {
      position: "absolute",
      //
      // Values determined experimentally 😞
      //
      top: "2rem",
      bottom: "-1rem",
      left: "0",
      right: "0",
      visibility: "hidden",
    });

    domRoot.parentElement.style.position = "relative";
    domRoot.parentElement.appendChild(limiterElement);

    limiterElements.set(root, limiterElement);
  }

  const balloon = editor.plugins.get(ContextualBalloon);

  balloon.positionLimiter = () => {
    //
    // Custom limiter callback for the balloon plugin, where we return the
    // element we created above instead of allowing it to use the editable's
    // root. The balloon plugin will use the bounds of the returned element to
    // determine best positioning for balloons.
    //
    const editableElement = view.document.selection.editableElement;
    if (editableElement == null) return null;

    return (
      limiterElements.get(editableElement.root as ViewRootEditableElement) ??
      null
    );
  };

  editor.on("destroy", () => {
    for (const element of limiterElements.values()) {
      element.remove();
    }
  });
};

export const allowArrowNavigationAtElementBoundaries = (editor: Editor) => {
  //
  // Enables arrow key navigation out of elements at the beginning or end of the document
  //

  editor.editing.view.document.on<ViewDocumentArrowKeyEvent>(
    "arrowKey",
    (e, data) => {
      const direction = getLocalizedArrowKeyCodeDirection(
        data.keyCode,
        editor.locale.contentLanguageDirection,
      );
      const range = editor.model.document.selection.getFirstRange();
      if (!range?.isCollapsed) return;

      const [position] = range.getPositions();
      const parent = position.parent;
      if (!parent.is("element")) return;

      //
      // If we're at the end or beginning of an element, set the selection to be
      // just outside the end or beginning of said element
      //
      // This will be overridden by the underlying keyboard handling except when
      // there's nowhere to go; so effectively this only does anything when at the
      // beginning or end of the content document
      //

      if (direction === "left" && position.isAtStart) {
        editor.model.change((writer) =>
          writer.setSelection(writer.createPositionBefore(parent)),
        );
      }
      if (direction === "right" && position.isAtEnd) {
        editor.model.change((writer) =>
          writer.setSelection(writer.createPositionAfter(parent)),
        );
      }
    },
    { priority: "high" },
  );
};

export const fixAutolinkEnterErrors = (editor: Editor) => {
  //
  // Pressing enter inside of an `isLimit` element causes an error in the AutoLink plugin
  // Suppress the keypress in that case
  //

  const enterCommand = editor.commands.get("enter");
  enterCommand?.on<CommandExecuteEvent>(
    "execute",
    (e) => {
      const container = editor.model.document.selection
        .getFirstRange()
        ?.getCommonAncestor();
      if (container == null) return;
      if (editor.model.schema.isLimit(container)) {
        e.stop();
      }
    },
    { priority: "highest" },
  );
};

const applyFixes = (editor: Editor) => {
  preventToolbarFlicker(editor);
  preventBalloonToolbarOverlap(editor);
  allowArrowNavigationAtElementBoundaries(editor);
  fixAutolinkEnterErrors(editor);
};

export default applyFixes;
